import type { ReactNode } from "react";
import { makeStyles, mergeClasses, shorthands, tokens, Text } from "@fluentui/react-components";

/**
 * Small design system for Meeting Angelo Lite.
 * Style: clean white cards, a thin colored accent line, tinted icon tiles and
 * dark text — modern and minimal (Azure / Fluent look). No filled/gradient cards.
 */

// --- Inline SVG icons (avoids the @fluentui/react-icons dependency) ----------

type IconProps = { className?: string };

export function SparkleIcon({ className }: IconProps) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z"
        fill="currentColor"
      />
      <path d="M19 14l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2z" fill="currentColor" opacity="0.7" />
    </svg>
  );
}

export function CalendarIcon({ className }: IconProps) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export function DocumentIcon({ className }: IconProps) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 3h8l4 4v14a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M14 3v4h4M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export function RocketIcon({ className }: IconProps) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14 4c3.5.5 6 3 6.5 6.5-2 4-6 8-9.5 9l-2.5-2.5C9 20.5 13 16.5 14 14.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20.5 10.5C17 11.5 13 15.5 12 19L9.5 16.5C10.5 13 14.5 9 18 8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="15.5" cy="8.5" r="1.4" fill="currentColor" />
      <path d="M7 17c-1.5 0-3 1.2-3 3 1.8 0 3-1.5 3-3z" fill="currentColor" />
    </svg>
  );
}

export function ClockIcon({ className }: IconProps) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export function ArrowRightIcon({ className }: IconProps) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ShieldIcon({ className }: IconProps) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3l7 3v5c0 4.4-3 8-7 10-4-2-7-5.6-7-10V6l7-3z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function BoltIcon({ className }: IconProps) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M13 2L5 13h5l-1 9 8-11h-5l1-9z" fill="currentColor" />
    </svg>
  );
}

export function TeamsIcon({ className }: IconProps) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="7" width="11" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M6 10h5M8.5 10v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="18" cy="7" r="2.4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M16 18c1.2 0 4 .3 4-2v-4h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// --- Styles ------------------------------------------------------------------

export const useUiStyles = makeStyles({
  page: {
    maxWidth: "720px",
    margin: "0 auto",
    padding: "20px 16px 40px",
  },
  section: {
    marginBottom: "22px",
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "10px",
  },
  sectionLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontSize: "11px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },
  countPill: {
    minWidth: "20px",
    height: "18px",
    padding: "0 6px",
    borderRadius: "9px",
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontSize: "11px",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    position: "relative",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    borderRadius: "10px",
    padding: "14px 16px",
    boxShadow: tokens.shadow2,
    transitionProperty: "box-shadow, transform, border-color",
    transitionDuration: "150ms",
  },
  cardInteractive: {
    cursor: "pointer",
    ":hover": {
      boxShadow: tokens.shadow8,
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
      transform: "translateY(-1px)",
    },
  },
  accentLeft: {
    "::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "10px",
      bottom: "10px",
      width: "3px",
      borderRadius: "3px",
      backgroundColor: tokens.colorBrandBackground,
    },
  },
  accentTop: {
    "::before": {
      content: '""',
      position: "absolute",
      left: "16px",
      right: "16px",
      top: 0,
      height: "3px",
      borderRadius: "3px",
      backgroundColor: tokens.colorBrandBackground,
    },
  },
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  },
  rowBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },
  grow: {
    minWidth: 0,
    flexGrow: 1,
  },
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  // Tinted icon tile
  iconTile: {
    flexShrink: 0,
    width: "38px",
    height: "38px",
    borderRadius: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
  },
  iconTileSuccess: {
    color: tokens.colorPaletteGreenForeground1,
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  iconTileNeutral: {
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "3px",
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
  },
  hero: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "16px",
    marginBottom: "22px",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    borderRadius: "12px",
    boxShadow: tokens.shadow2,
    position: "relative",
    overflow: "hidden",
  },
  heroAccent: {
    "::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: "4px",
      backgroundColor: tokens.colorBrandBackground,
    },
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    height: "22px",
    padding: "0 9px",
    borderRadius: "11px",
    fontSize: "11px",
    fontWeight: 600,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  emptyCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    color: tokens.colorNeutralForeground2,
  },
  footer: {
    textAlign: "center",
    marginTop: "24px",
    color: tokens.colorNeutralForeground4,
  },
  landingWrap: {
    maxWidth: "560px",
    margin: "0 auto",
    padding: "32px 16px 48px",
  },
  brandMark: {
    width: "56px",
    height: "56px",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
    margin: "0 auto",
  },
  featureRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "12px 0",
  },
  divider: {
    height: "1px",
    backgroundColor: tokens.colorNeutralStroke2,
    margin: "2px 0",
  },
  stepNum: {
    flexShrink: 0,
    width: "24px",
    height: "24px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 700,
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
  },
});

// --- Composable primitives ---------------------------------------------------

export function SectionTitle({ children, count }: { children: ReactNode; count?: number }) {
  const s = useUiStyles();
  return (
    <div className={s.sectionHead}>
      <span className={s.sectionLabel}>{children}</span>
      {typeof count === "number" && count > 0 && <span className={s.countPill}>{count}</span>}
    </div>
  );
}

export function IconTile({
  children,
  variant = "brand",
}: {
  children: ReactNode;
  variant?: "brand" | "success" | "neutral";
}) {
  const s = useUiStyles();
  return (
    <div
      className={mergeClasses(
        s.iconTile,
        variant === "success" && s.iconTileSuccess,
        variant === "neutral" && s.iconTileNeutral,
      )}
    >
      {children}
    </div>
  );
}

export function MetaRow({ children }: { children: ReactNode }) {
  const s = useUiStyles();
  return <div className={s.metaRow}>{children}</div>;
}

export function Footer({ children }: { children: ReactNode }) {
  const s = useUiStyles();
  return (
    <Text size={100} block className={s.footer}>
      {children}
    </Text>
  );
}
